import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, ExternalLink, Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/billing/StatusBadge";
import { db, formatDate, invoiceDownloadHref, invoiceTotalCents, money, type InvoiceRecord } from "@/lib/billing";

async function fetchInvoices(): Promise<InvoiceRecord[]> {
  const { data, error } = await db.from("invoices").select("*, packages(name)").order("created_at", { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export default function AdminInvoicesPage() {
  const [search, setSearch] = useState("");
  const { data: invoices = [], isLoading } = useQuery({ queryKey: ["admin-invoices"], queryFn: fetchInvoices });
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return invoices.filter((i) => [i.invoice_number, i.status, i.user_id, i.packages?.name].join(" ").toLowerCase().includes(q));
  }, [invoices, search]);

  const exportCsv = () => {
    const rows = filtered.map((i) => ({
      invoice: i.invoice_number ?? "",
      user_id: i.user_id ?? "",
      package: i.packages?.name ?? "",
      amount: invoiceTotalCents(i) / 100,
      currency: i.currency,
      status: i.status,
      billing_cycle: i.billing_cycle ?? "",
      subscription_id: i.subscription_id ?? "",
      stripe_session_id: i.stripe_checkout_session_id ?? "",
      email_status: i.invoice_email_status ?? "",
      due_date: i.due_date ?? "",
      paid_at: i.paid_at ?? "",
      hosted_invoice_url: i.hosted_invoice_url ?? "",
    }));
    const csv = [Object.keys(rows[0] ?? {}).join(","), ...rows.map((r) => Object.values(r).map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "delapresse-invoices.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 px-6 backdrop-blur-xl">
        <div>
          <h1 className="text-lg font-semibold text-foreground">Invoices</h1>
          <p className="text-xs text-muted-foreground">All Stripe invoices with package and ownership context.</p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={!filtered.length}><Download className="mr-2 h-4 w-4" />Export CSV</Button>
      </header>
      <main className="p-6">
        <Card>
          <CardHeader className="gap-3">
            <CardTitle className="text-base">Invoice ledger</CardTitle>
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" placeholder="Filter by user, package, status, or invoice..." value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? <div className="flex h-48 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div> : (
              <Table>
                <TableHeader><TableRow><TableHead>Invoice</TableHead><TableHead>User</TableHead><TableHead>Package</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead>Cycle</TableHead><TableHead>Paid</TableHead><TableHead>Email</TableHead><TableHead className="text-right">Links</TableHead></TableRow></TableHeader>
                <TableBody>
                  {filtered.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell className="font-medium">{invoice.invoice_number ?? "Pending"}</TableCell>
                      <TableCell className="max-w-[180px] truncate text-xs text-muted-foreground">{invoice.user_id ?? "Unknown"}</TableCell>
                      <TableCell>{invoice.packages?.name ?? "Unknown"}</TableCell>
                      <TableCell>
                        <div>{money(invoiceTotalCents(invoice), invoice.currency, true)}</div>
                        <div className="text-xs text-muted-foreground">Tax {money(invoice.tax_amount ?? 0, invoice.currency, true)}</div>
                      </TableCell>
                      <TableCell><StatusBadge status={invoice.status} /></TableCell>
                      <TableCell>{invoice.billing_cycle ?? "monthly"}</TableCell>
                      <TableCell>{formatDate(invoice.paid_at)}</TableCell>
                      <TableCell>{invoice.invoice_email_status ?? "not sent"}</TableCell>
                      <TableCell className="space-x-2 text-right">
                        {invoice.hosted_invoice_url && <Button asChild size="sm" variant="outline"><a href={invoice.hosted_invoice_url} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4" /></a></Button>}
                        {invoiceDownloadHref(invoice) && (
                          <Button asChild size="sm" variant="outline">
                            <a
                              href={invoiceDownloadHref(invoice) ?? "#"}
                              download={invoice.generated_invoice_pdf_filename ?? undefined}
                              target={invoice.generated_invoice_pdf_base64 ? undefined : "_blank"}
                              rel={invoice.generated_invoice_pdf_base64 ? undefined : "noreferrer"}
                            >
                              <Download className="h-4 w-4" />
                            </a>
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
